export const DAY_MS = 24 * 60 * 60 * 1000;

// Closed vocab of LLM-assigned regions, in the worker's priority order. Single
// source of truth for the news-page region filter: the NewsFiltersBar <select>
// options and the News.vue deep-link validator both derive from this. Values
// mirror the worker's VALID_GEOGRAPHIC_REGION_VALUES and the backend
// GeographicRegion Literal — keep the three in lockstep.
export const NEWS_REGIONS = ['dutch', 'europe', 'north_america', 'other'];

// Map the news page's period dropdown values to an inclusive [start, end] range
// that the backend can apply as an ES `range` filter on `timestamp`.
// Returning Date objects (or null for 'all') keeps the caller free to format
// them however the API expects — see toIsoDate below.
//
// The 'older' bucket caps at 100 days because the backend's default window is
// 100 days. Going beyond would either need a wider backend window or a
// dedicated 'all-time' opt-in. See the news page redesign plan for context.
export const periodToDateRange = (period, now = Date.now()) => {
  const end = new Date(now);
  if (period === 'last_24h') return { start: new Date(now - 1 * DAY_MS), end };
  if (period === 'last_7d') return { start: new Date(now - 7 * DAY_MS), end };
  if (period === 'last_30d') return { start: new Date(now - 30 * DAY_MS), end };
  if (period === 'older') {
    return { start: new Date(now - 100 * DAY_MS), end: new Date(now - 30 * DAY_MS) };
  }
  return { start: null, end: null };
};

// YYYY-MM-DD in local time. The backend localizes to Europe/Amsterdam, which
// matches the page's audience; UTC dates would shift the boundary by an hour
// across DST and surprise users at midnight.
export const toIsoDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// Build the `/news` (article list) query params from the current filter state.
// Pure + exported so the FE→API wiring — in particular that a selected region
// reaches the backend as `geographic_region` — is unit-testable without mounting
// the whole page. `state` carries the resolved filter values; `extra` folds in
// caller-owned params like `size` / `from_offset`.
export const buildNewsQueryParams = (state = {}, extra = {}) => {
  const params = { ...extra };
  if (state.languages?.length) params.languages = state.languages.join(',');
  if (state.category) {
    params.category = state.category;
  } else if (state.groupSlugs && state.groupSlugs.length) {
    // Theme group: filter server-side on all of the group's slugs.
    params.categories = state.groupSlugs.join(',');
  }
  if (state.source) params.publisher_name = state.source;
  if (state.region) params.geographic_region = state.region;
  // v3 coverage + taxonomy (PI-262 / PI-272). Sent as the backend's own
  // identifiers — no client-side expansion of a group into its topics, because
  // the backend owns that mapping now and expanding it here is exactly the
  // drift categoryGroups.js used to carry.
  if (state.countries && state.countries.length) {
    params.countries = state.countries.join(',');
  }
  if (state.scope) params.geographic_scope = state.scope;
  if (state.topicGroup) params.topic_group = state.topicGroup;
  if (state.topics && state.topics.length) params.topics = state.topics.join(',');
  const trimmedQuery = String(state.query || '').trim();
  if (trimmedQuery) params.q = trimmedQuery;
  const { start, end } = periodToDateRange(state.timeWindow);
  const startIso = toIsoDate(start);
  const endIso = toIsoDate(end);
  if (startIso) params.start_date = startIso;
  if (endIso) params.end_date = endIso;
  return params;
};

// The `/news` filters of `buildNewsQueryParams`, applied to an item already loaded: Profiteia's
// own articles are fetched apart from the feed and mixed in, so the server never filtered them.
// Mirrors the server's semantics (category or theme group, publisher, region, coverage, free
// text over title and description, period).
export const matchesNewsFilters = (item, state = {}, now = Date.now()) => {
  if (state.languages?.length && item.language && !state.languages.includes(item.language)) return false;
  if (state.category && item.category !== state.category) return false;
  if (!state.category && state.groupSlugs?.length && !state.groupSlugs.includes(item.category)) return false;
  if (state.source && item.publisherName !== state.source) return false;
  if (state.region && item.geographicRegion !== state.region) return false;
  if (state.scope && item.geographicScope !== state.scope) return false;
  if (state.countries?.length && !state.countries.some((code) => (item.countries || []).includes(code))) return false;
  const query = String(state.query || '').trim().toLowerCase();
  if (query && !`${item.title} ${item.description}`.toLowerCase().includes(query)) return false;
  const { start, end } = periodToDateRange(state.timeWindow, now);
  if (start && item.timestampMs < start.getTime()) return false;
  if (end && item.timestampMs > end.getTime()) return false;
  return true;
};

// Params for the front page's business-source boost fetch: a second, small
// /news request restricted to BUSINESS_SOURCE_NAMES. Needed because the main
// fetch pages the whole index by recency — high-volume consumer outlets fill
// the first page within hours, so business-source articles usually aren't in
// the loaded set at all and no client-side sort could surface them. Shares
// buildNewsQueryParams' gating; in practice only region/period apply — the
// boost is fetched exclusively on the front page (category/group tabs never
// render it) and never with the single-source or free-text filters (callers
// skip the boost entirely in those modes).
export const buildBusinessBoostParams = (state = {}, extra = {}) => {
  const params = buildNewsQueryParams({ ...state, source: '', query: '' }, extra);
  params.publisher_names = BUSINESS_SOURCE_NAMES.join(',');
  return params;
};

// Params for the `/news/sources` dropdown aggregation. Same category / region /
// period gating as the article list (so the dropdown matches the feed) but
// WITHOUT the source + free-text filters — the available sources must not depend
// on which source the user already picked. Composed from buildNewsQueryParams
// (source/query blanked) so list/dropdown parity is structural, not
// copy-discipline.
export const buildSourcesQueryParams = (state = {}) =>
  buildNewsQueryParams({ ...state, source: '', query: '' });

// Publishers deemed business-relevant — their articles lead the front page.
// This per-SOURCE tier works alongside the per-article business_relevant flag
// (see businessRank).
// EXACT case as in worker/data/json/validated_feeds.json `source` labels —
// buildBusinessBoostParams sends these verbatim to the API, where they hit a
// case-sensitive `publisher_name.keyword` terms filter. Keep in sync with
// that file when adding/removing an ingested source: a rename on either side
// silently drops that source from the boost (the spec pins these exact
// strings, so at least the frontend copy can't drift unnoticed).
export const BUSINESS_SOURCE_NAMES = [
  'enspired Trading',
  'Energy-Storage.News',
  'Brady Technologies', // feed label; the publisher aggregates Commodities People content
  'Global Hydrogen Hub',
  'EP NL',
  'Timera Energy',
  'ENTSO-E',
  'LNG Prime',
  'Centrica Business Solutions',
  'BloombergNEF',
  'Solar Magazine',
  'Offshore Energy',
  'Energeia',
  'Energie Nederland',
];

// Lowercased lookup set for ranking loaded articles (case-insensitive there,
// since it only compares against our own normalized publisherName). NOT to be
// confused with the per-article `businessRelevant` flag — this is purely the
// source list above in lowercase.
export const BUSINESS_SOURCE_NAMES_LOWER = new Set(
  BUSINESS_SOURCE_NAMES.map((name) => name.toLowerCase()),
);

export const isBusinessSource = (item) =>
  BUSINESS_SOURCE_NAMES_LOWER.has(String(item?.publisherName || '').trim().toLowerCase());

// Front-page priority tier — lower rank = higher in the feed:
//  0 = article from a BUSINESS_SOURCE_NAMES publisher. Source precedence
//      is STRICT and unconditional: the dedicated B2B feeds always lead, and
//      a mainstream article can never outrank them however fresh or well-
//      illustrated it is (that was tried with a shared tier — high-volume
//      consumer outlets with hourly, image-rich posts drowned the B2B feeds).
//      businessRelevant === false does not demote a business source's article.
//  1 = article from any other source whose ES `business_relevant` flag is
//      true (LLM business_relevance_score cleared the worker's threshold) —
//      strong B2B stories from general outlets still beat the plain feed.
//  2 = everything else.
// Prefers the `businessSource` flag precomputed by normalizeItem (avoids
// re-lowercasing on every comparison), falling back to a live lookup for
// un-normalized items (e.g. unit-test fixtures).
export const businessRank = (item) => {
  const fromBusinessSource =
    typeof item?.businessSource === 'boolean' ? item.businessSource : isBusinessSource(item);
  if (fromBusinessSource) return 0;
  if (item?.businessRelevant === true) return 1;
  return 2;
};

export const normalizeItem = (item, index) => {
  const timestampMs = new Date(item?.timestamp || 0).getTime();
  const timestamp = Number.isFinite(timestampMs) ? new Date(timestampMs).toISOString() : null;
  let domain = '';
  try {
    if (item?.url || item?.link) {
      domain = new URL(item.url || item.link).hostname.replace(/^www\./, '');
    }
  } catch (error) {
    domain = '';
  }

  const publisherName = String(item?.publisher_name || item?.source || '').trim();

  return {
    id: item?.id || `${item?.timestamp || 'ts'}-${index}`,
    language: item?.language || null,
    title: String(item?.title || '').trim(),
    source: String(item?.source || '').trim(),
    link: String(item?.url || item?.link || '').trim(),
    domain,
    timestamp,
    timestampMs: Number.isFinite(timestampMs) ? timestampMs : 0,
    publisherName,
    // Precompute the business-source tier once here, so the front-page
    // business-first sort doesn't re-lowercase + Set-lookup the publisher on
    // every comparison (the sort runs O(n log n) over the whole loaded set on
    // each pagination append). businessRank reads this flag. See below.
    businessSource: BUSINESS_SOURCE_NAMES_LOWER.has(publisherName.toLowerCase()),
    geographicRegion: typeof item?.geographic_region === 'string' ? item.geographic_region : null,
    // v3 coverage. Preserve null vs. [] — "classified as global" and "not
    // classified yet" render differently, same rule as the v2 scores below.
    countries: Array.isArray(item?.countries) ? item.countries : null,
    geographicScope:
      typeof item?.geographic_scope === 'string' ? item.geographic_scope : null,
    topicGroup: typeof item?.topic_group === 'string' ? item.topic_group : null,
    topics: Array.isArray(item?.topics) ? item.topics : null,
    // Ingest time, distinct from the publication date in `timestamp`.
    fetchedAt: typeof item?.fetched_at === 'string' ? item.fetched_at : null,
    description: String(item?.description || '').trim(),
    imageUrl: item?.image_url || null,
    energyRelevant: Boolean(item?.energy_relevant),
    relevanceScore: Number(item?.relevance_score) || 0,
    category: String(item?.category || 'unclassified').trim(),
    // v2 (business-relevance) fields. Preserve null vs. boolean/number so the
    // hero selector can distinguish "no opinion" (legacy doc) from
    // "explicitly false" (v2-classified consumer doc). Do NOT coerce missing
    // values to 0 / false — that would collapse those two cases.
    businessRelevant:
      typeof item?.business_relevant === 'boolean' ? item.business_relevant : null,
    businessRelevanceScore:
      typeof item?.business_relevance_score === 'number' ? item.business_relevance_score : null,
    consumerFocusScore:
      typeof item?.consumer_focus_score === 'number' ? item.consumer_focus_score : null,
    marketImpactScore:
      typeof item?.market_impact_score === 'number' ? item.market_impact_score : null,
    classificationConfidence:
      typeof item?.classification_confidence === 'number' ? item.classification_confidence : null,
    audience: typeof item?.audience === 'string' ? item.audience : null,
    classificationLabels: Array.isArray(item?.classification_labels)
      ? item.classification_labels
      : null,
    classificationReason:
      typeof item?.classification_reason === 'string' ? item.classification_reason : null,
    classificationSchemaVersion:
      typeof item?.classification_schema_version === 'number'
        ? item.classification_schema_version
        : null,
  };
};

export const sortByImageThenRecent = (a, b) => {
  const byImage = Number(Boolean(b.imageUrl)) - Number(Boolean(a.imageUrl));
  if (byImage !== 0) return byImage;
  return b.timestampMs - a.timestampMs;
};

// Main-page ordering: business sources first, then business-scored articles
// from other sources, then the rest (see businessRank), falling back to the
// existing image-then-recency ordering within each tier. Deliberately NOT
// ordered by the raw business_relevance_score inside a tier: sub-0.1 score
// differences are LLM noise, and score-ordering would pin stale high-scorers
// above fresh news. The tiers decide placement; freshness + imagery decide
// within them. The "Latest" tab and the latest rail keep their pure-recency
// sort and do NOT use this.
export const sortByBusinessThenImageThenRecent = (a, b) => {
  const byBusiness = businessRank(a) - businessRank(b);
  if (byBusiness !== 0) return byBusiness;
  return sortByImageThenRecent(a, b);
};

// Append `incoming` items to `existing`, skipping any whose id is already
// present. Offset pagination uses the post-filter list length as from_offset,
// but the server drops timestamp-less docs and the client drops untitled/undated
// rows, so consecutive ES windows overlap and can re-return an already-shown
// article. Concatenating blindly would render the same id twice (Vue
// duplicate-key warning + visible duplicate); this keeps the feed unique.
export const appendUniqueById = (existing, incoming) => {
  const seen = new Set(existing.map((item) => item.id));
  return [...existing, ...incoming.filter((item) => !seen.has(item.id))];
};

// Items for the front-page card grid: drop the hero article ONLY when the hero
// banner is actually rendered (front page, not filtering). While a filter is
// active the banner is suppressed, so stripping the hero id here would make a
// matching article disappear from the results entirely.
export const frontPageGridItems = (items, heroId, isFiltering) => {
  if (!heroId || isFiltering) return items;
  return items.filter((item) => item.id !== heroId);
};

// Mirrors the *verified-working* server-side rewrites in
// worker/helpers/rss_parser_helper.py. WordPress capped URLs are intentionally
// excluded: the parser rewrites them to `-1536x{H}` proportionally, but on some
// hosts (Petrochem, Energeia) that exact variant doesn't exist and 404s. Those
// articles still display fine when picked organically — they just shouldn't be
// promoted to the hero slot, where a 404 would show the gradient fallback.
export const hasUpgradedImage = (url) => {
  if (!url) return false;
  if (url.includes('/feature-crop/1920/1080/')) return true;
  if (url.includes('-1920x1080.')) return true;
  if (url.includes('width=1920&height=1080')) return true;
  // Profiteia's own article photos: always generated at 1536x1024.
  if (/\/public\/news\/[^/]+\/hero\.jpg/.test(url)) return true;
  return false;
};

export const selectHeroArticle = (items, now = Date.now()) => {
  if (!Array.isArray(items) || items.length === 0) return null;

  const todayStart = new Date(now);
  todayStart.setHours(0, 0, 0, 0);
  const todayMs = todayStart.getTime();
  const byScore = (a, b) => b.relevanceScore - a.relevanceScore;

  // Two candidate pools:
  //  - businessPool: v2-classified docs explicitly marked business-relevant.
  //  - broaderPool:  business pool PLUS legacy/unscored docs that v1 already
  //                  marked energy-relevant (and that v2 has not explicitly
  //                  rejected as consumer — businessRelevant !== false).
  // v2-consumer docs (businessRelevant === false) are NEVER eligible for hero.
  const businessPool = items.filter((i) => i.businessRelevant === true);
  const broaderPool = items.filter(
    (i) => i.businessRelevant === true || (i.energyRelevant && i.businessRelevant !== false),
  );
  if (!broaderPool.length) return null;

  // Helper: cascade through (today + upgraded image) → (48h + upgraded image)
  // within a given pool. Returns null when no candidate has an upgraded image.
  const pickUpgraded = (pool) => {
    if (!pool.length) return null;
    const today = pool
      .filter((i) => i.timestampMs >= todayMs && i.imageUrl)
      .sort(byScore);
    const todayUpgraded = today.filter((i) => hasUpgradedImage(i.imageUrl));
    if (todayUpgraded.length) return todayUpgraded[0];

    const recent = pool
      .filter((i) => i.timestampMs >= now - 48 * 3600000 && i.imageUrl)
      .sort(byScore);
    const recentUpgraded = recent.filter((i) => hasUpgradedImage(i.imageUrl));
    if (recentUpgraded.length) return recentUpgraded[0];

    return null;
  };

  // Step 1: best-quality business article (today/recent + upgraded image).
  const businessUpgraded = pickUpgraded(businessPool);
  if (businessUpgraded) return businessUpgraded;

  // Step 2: no business article has an upgraded image → broaden to legacy
  // energy_relevant articles before degrading image quality. This preserves
  // the page's visual standard at the cost of accepting a non-v2 article when
  // v2-business coverage doesn't have one with a good image yet.
  const broaderUpgraded = pickUpgraded(broaderPool);
  if (broaderUpgraded) return broaderUpgraded;

  // Step 3: soft fallback — no upgraded image anywhere relevant. Prefer
  // business pool with ANY image, then broader pool, then drop the image
  // requirement entirely. Hero never disappears as long as at least one
  // relevant doc exists.
  const pickSoft = (pool) => {
    if (!pool.length) return null;
    const today = pool
      .filter((i) => i.timestampMs >= todayMs && i.imageUrl)
      .sort(byScore);
    if (today.length) return today[0];
    const recent = pool
      .filter((i) => i.timestampMs >= now - 48 * 3600000 && i.imageUrl)
      .sort(byScore);
    if (recent.length) return recent[0];
    return [...pool].sort(byScore)[0] || null;
  };

  return pickSoft(businessPool) || pickSoft(broaderPool);
};
